import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../SvgIcon";
import VideoUpload from "../../../inputs/video-upload";
import RichTextInput from "../../../inputs/rich-text-input";
import SelectExerciseTypeMuscleGroups from "../../../inputs/select-exercise-type-muscle-groups";
import {
  EXERCISE_TYPE,
  PRIMARY_MUSCLE_GROUP,
  SECONDARY_MUSCLE_GROUP,
} from "../../../../constants";
import {
  buildLibraryMetadataSavePayload,
  getMetadataMissingKeys,
  libraryMetadataValuesFromRecord,
  metadataMissingKeysSignature,
} from "../../../../utils/exercise-library-metadata";
import { resolveVideoSource } from "../../../../utils/exercise-video-preview";
import { fetchLibraryMetadataAction } from "../../../../utils/submit-library-metadata-from-add-exercise";
import ExerciseVideoPreview from "./ExerciseVideoPreview";

/** Id shared with library modals for `form=` on metadata inputs (Add Exercise uses fetch on save). */
export const ADD_EXERCISE_LIBRARY_METADATA_FORM_ID =
  "add-exercise-library-metadata-save";

/** CSS class for a metadata field row; highlights catalog gaps. */
function fieldClassName(isMissing) {
  return isMissing
    ? "form-row-item Add-Exercise-metadataField--missing"
    : "form-row-item";
}

function FieldLabel({ label, isMissing }) {
  return (
    <label className="Add-Exercise-metadataField-label">
      <span>{label}</span>
      {isMissing ? (
        <SvgIcon
          src="/img/new-icons/chip-warning.svg"
          classNames="Add-Exercise-metadataField-warningIcon"
          noFilterOnLight
        />
      ) : null}
    </label>
  );
}

FieldLabel.propTypes = {
  label: PropTypes.string.isRequired,
  isMissing: PropTypes.bool,
};

/**
 * Editable catalog fields for a library-backed exercise in the Add Exercise modal.
 *
 * ## Why this component exists
 *
 * When the coach picks a library exercise with missing catalog metadata (muscle groups,
 * exercise type, video, instructions), this panel lets them fill gaps and persist those
 * changes back to the master library on Save — without leaving Add Exercise.
 *
 * ## Two POSTs on Save (when the coach changed something)
 *
 * Add Exercise already owns a main form (`#add-exercise`) for the workout exercise.
 * Library metadata uses a **separate** server action:
 *
 * - Secondary / assistant → `POST /actions/user/edit-secondary/:index`
 * - Primary lifts → `POST /actions/rep-maximum/update-primary`
 *
 * Routing, body shape, and validation live in `submit-library-metadata-from-add-exercise.js`
 * (`postEditSecondaryLibraryMetadata` / `postUpdatePrimaryLibraryMetadata`). This component
 * only owns the client transport: Rill `fetch` with draft body + optional `files.videoFile`.
 *
 * On Save, `add-exercise` `handleSubmit` runs library save **first** (via ref), then the
 * normal exercise submit. If nothing changed in the panel, the library step is skipped entirely
 * (`shouldSave` in `buildLibraryMetadataSavePayload`).
 *
 * ## Rill POST (transport layer)
 *
 * We do **not** call `requestSubmit()` on a hidden form. Instead `submitLibraryMetadata`
 * collects the video `File` and delegates to `fetchLibraryMetadataAction` in
 * `submit-library-metadata-from-add-exercise.js`, which uses Rill's document `fetch`
 * (same router as a normal library modal form submit). See that file for the full
 * explanation of why detached forms failed for video uploads.
 *
 * ## Why `form=` on panel inputs (not a nested `<form>`)
 *
 * HTML forbids nesting `<form>` elements. The metadata panel renders **inside** `#add-exercise`,
 * so we cannot wrap it in a second `<form>` in the same DOM subtree. Panel inputs use
 * `form={ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}` for parity with `add-secondary-movement`;
 * library save posts draft values via Rill client `fetch`, not native form submit.
 *
 * ## Ref API (draft stays local — parent does not re-render on keystroke)
 *
 * `add-exercise` is a class component; it reads this panel only on Save through a ref chain:
 * `ExerciseLibraryMetadataGapsNotice` → `ExerciseLibraryMetadataFields`.
 *
 * - `getSavePayload()` — normalized draft + computed `shouldSave` for body building.
 * - `submitLibraryMetadata(url, body, req)` — Rill POST via `fetchLibraryMetadataAction`.
 *
 * Draft lives in `useState` here so typing in metadata fields does not force Add Exercise
 * to re-render the full modal.
 *
 * @see ADD_EXERCISE_LIBRARY_METADATA_FORM_ID
 * @see js/utils/submit-library-metadata-from-add-exercise.js
 */
const ExerciseLibraryMetadataFields = forwardRef(
  function ExerciseLibraryMetadataFields(
    {
      record,
      libraryIndex,
      formPrimary,
      isPrimaryExercise,
      repMaximum,
      athleteId,
      isOpen,
      onMissingKeysChange,
    },
    ref
  ) {
    const { t } = useTranslation("coaching-portal");
    /** File bytes for multipart POST (draft.videoFile is display path string for VideoUpload). */
    const videoFileRef = useRef(null);
    /** Same `File` as videoFileRef, kept in state so the video preview can play it. */
    const [pickedFile, setPickedFile] = useState(null);
    const videoSectionRef = useRef(null);
    const [draft, setDraft] = useState(() =>
      libraryMetadataValuesFromRecord(record)
    );

    const activeMissingKeys = useMemo(
      () => getMetadataMissingKeys(draft),
      [draft]
    );
    const missingSet = new Set(activeMissingKeys);
    const isMissing = (key) => missingSet.has(key);
    const recordSignature = useMemo(
      () => JSON.stringify(libraryMetadataValuesFromRecord(record)),
      [record]
    );
    // Stable ref for onMissingKeysChange — avoids effect churn when parent re-renders.
    const onMissingKeysChangeRef = useRef(onMissingKeysChange);
    onMissingKeysChangeRef.current = onMissingKeysChange;

    const draftMissingKeysSignature = useMemo(
      () => metadataMissingKeysSignature(activeMissingKeys),
      [activeMissingKeys]
    );

    // Reset draft when library record changes (parent `key` also remounts on exercise change).
    useEffect(() => {
      setDraft(libraryMetadataValuesFromRecord(record));
      videoFileRef.current = null;
      setPickedFile(null);
    }, [recordSignature]);

    // Notify parent chip when draft missing keys change (signature avoids reference loops).
    useEffect(() => {
      onMissingKeysChangeRef.current?.(activeMissingKeys);
    }, [draftMissingKeysSignature]);

    /**
     * Ref API for add-exercise Save: draft snapshot + Rill library POST.
     * @see fetchLibraryMetadataAction in submit-library-metadata-from-add-exercise.js
     */
    useImperativeHandle(
      ref,
      () => ({
        /** Normalized payload for `saveLibraryMetadataIfRequested` (draft + library ids; computes shouldSave). */
        getSavePayload() {
          return buildLibraryMetadataSavePayload({
            draft,
            record,
            libraryIndex,
            formPrimary,
            isPrimaryExercise,
            repMaximum,
          });
        },
        /**
         * Second step of library save: POST to Rill action with body + optional video file.
         *
         * Called from `dispatchLibrarySubmit` after this util builds `url` and `body`.
         * `req` is forwarded from add-exercise (`this.context.req`) so we can reach Rill's
         * in-browser router without importing `@rill/http/adapter/document` here (SSR-safe).
         *
         * @param {string} url — `/actions/user/edit-secondary/:index` or `/actions/rep-maximum/update-primary`
         * @param {Record<string, string>} body — full action POST body (movement, muscles, source, …)
         * @param {object} req — Rill request from add-exercise context
         * @returns {Promise<unknown>}
         */
        submitLibraryMetadata(url, body, req) {
          // draft.videoFile is a display string for VideoUpload; bytes live in videoFileRef.
          const files = {};
          const videoFile =
            videoFileRef.current ||
            (typeof document !== "undefined"
              ? document.querySelector(
                  `input[type="file"][name="videoFile"][form="${ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}"]`
                )?.files?.[0]
              : null);
          if (videoFile) {
            files.videoFile = videoFile;
          }
          return fetchLibraryMetadataAction(
            req,
            url,
            body,
            Object.keys(files).length ? files : undefined
          );
        },
      }),
      [draft, record, libraryIndex, formPrimary, isPrimaryExercise, repMaximum]
    );

    /** Sync draft from controlled inputs (selects, textarea, video upload). */
    const handleInputChange = (event) => {
      const { name, value, files } = event.target;
      // Same as add-secondary-movement: videoFile in draft is the input value string (path).
      // File bytes for the Rill POST are kept in videoFileRef (see submitLibraryMetadata).
      if (name === "videoFile") {
        videoFileRef.current = files?.[0] || null;
        setPickedFile(files?.[0] || null);
        setDraft((prev) => ({
          ...prev,
          videoFile: files?.[0] ? value || files[0].name : value,
          videoUrl: files?.[0] ? "" : prev.videoUrl,
        }));
        return;
      }
      setDraft((prev) => ({ ...prev, [name]: value }));
    };

    /** Clear video URL, file, and library original in draft. */
    const cleanVideoField = () => {
      videoFileRef.current = null;
      setPickedFile(null);
      setDraft((prev) => ({
        ...prev,
        videoOriginal: "",
        videoFile: null,
        videoUrl: "",
      }));
    };

    const videoSource = useMemo(
      () =>
        resolveVideoSource({
          videoUrl: draft.videoUrl,
          videoFile: draft.videoFile,
          videoOriginal: draft.videoOriginal,
          athleteId,
        }),
      [draft.videoUrl, draft.videoFile, draft.videoOriginal, athleteId]
    );

    /** "Change video" / "Add video": bring the coach to the URL and upload fields below. */
    const focusVideoFields = () => {
      const section = videoSectionRef.current;
      if (!section) {
        return;
      }
      section.scrollIntoView({ behavior: "smooth", block: "center" });
      section.querySelector("input.url-input:not([disabled])")?.focus({
        preventScroll: true,
      });
    };

    return (
      <div className="Add-Exercise-metadataDetails-panel">
          <ExerciseVideoPreview
            key={`${videoSource.kind}|${videoSource.displayName}|${videoSource.playUrl}`}
            source={videoSource}
            pickedFile={pickedFile}
            enabled={Boolean(isOpen)}
            onChangeVideo={focusVideoFields}
          />

          <div className="form-row">
            <div className={fieldClassName(isMissing("primaryMuscleGroup"))}>
              <FieldLabel
                label={t("extraInput.primaryMuscleGroup")}
                isMissing={isMissing("primaryMuscleGroup")}
              />
              <SelectExerciseTypeMuscleGroups
                value={draft.primaryMuscleGroup}
                handleChange={handleInputChange}
                type={PRIMARY_MUSCLE_GROUP}
                form={ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}
              />
            </div>
            <div className={fieldClassName(isMissing("secondaryMuscleGroup"))}>
              <FieldLabel
                label={t("extraInput.secondaryMuscleGroup")}
                isMissing={isMissing("secondaryMuscleGroup")}
              />
              <SelectExerciseTypeMuscleGroups
                value={draft.secondaryMuscleGroup}
                handleChange={handleInputChange}
                type={SECONDARY_MUSCLE_GROUP}
                form={ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}
              />
            </div>
          </div>

          <div className="form-row">
            <div className={fieldClassName(isMissing("exerciseTypeField"))}>
              <FieldLabel
                label={t("extraInput.exerciseType")}
                isMissing={isMissing("exerciseTypeField")}
              />
              <SelectExerciseTypeMuscleGroups
                value={draft.exerciseTypeField}
                handleChange={handleInputChange}
                type={EXERCISE_TYPE}
                form={ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}
              />
            </div>
          </div>

          <div
            ref={videoSectionRef}
            className={`Add-Exercise-metadataVideo${
              isMissing("video") ? " Add-Exercise-metadataField--missing" : ""
            }`}
          >
            <VideoUpload
              videoUrl={draft.videoUrl}
              videoFile={draft.videoFile}
              videoOriginal={draft.videoOriginal}
              cleanVideoField={cleanVideoField}
              handleInputChange={handleInputChange}
              showMissingIndicators={isMissing("video")}
              form={ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}
            />
          </div>

          <div className="form-row">
            <div className={fieldClassName(isMissing("instructions"))}>
              <FieldLabel
                label={t("extraInput.instructions")}
                isMissing={isMissing("instructions")}
              />
              {/* `key` re-seeds the editor when the draft is reset for a
                  different library record (see the recordSignature effect). */}
              <RichTextInput
                key={recordSignature}
                value={draft.instructions}
                name="instructions"
                form={ADD_EXERCISE_LIBRARY_METADATA_FORM_ID}
                handleInputChange={handleInputChange}
              />
            </div>
          </div>

          <p className="Add-Exercise-metadataDetails-note">
            {t("addExerciseModal.metadataLibraryAutoSaveNote")}
          </p>
        </div>
    );
  }
);

ExerciseLibraryMetadataFields.propTypes = {
  record: PropTypes.object,
  libraryIndex: PropTypes.number,
  formPrimary: PropTypes.string,
  isPrimaryExercise: PropTypes.bool,
  repMaximum: PropTypes.number,
  athleteId: PropTypes.string,
  isOpen: PropTypes.bool,
  onMissingKeysChange: PropTypes.func,
};

export default ExerciseLibraryMetadataFields;
